/**
 * MÚSICA PROCEDURAL POR REGIÃO (10.5D) — sem arquivos. Cada região tem um "clima"
 * (escala/andamento/timbre); renderizamos um loop curto via OfflineAudioContext e
 * tocamos em loop pelo canal de música (volume respeitado). Trocar de região troca o loop.
 */

interface RegionMood {
  root: number; // frequência da fundamental (Hz)
  scale: number[]; // graus em semitons (para o arpejo)
  chord: number[]; // graus do acorde de pad (semitons)
  tempo: number; // BPM
  arpType: OscillatorType;
  padType: OscillatorType;
}

// clima por região (léxico permitido; nada de nomes demoníacos)
const MOODS: Record<string, RegionMood> = {
  forgottenForest: { root: 146.83, scale: [0, 3, 5, 7, 10, 12], chord: [0, 3, 7], tempo: 62, arpType: "triangle", padType: "sine" }, // D menor, calmo/misterioso
  shatteredWilds: { root: 130.81, scale: [0, 2, 3, 7, 8, 12], chord: [0, 3, 7], tempo: 82, arpType: "triangle", padType: "sine" }, // tenso
  tempestReach: { root: 164.81, scale: [0, 2, 5, 7, 9, 12], chord: [0, 4, 7], tempo: 92, arpType: "sawtooth", padType: "sine" }, // épico/tempestade
  default: { root: 146.83, scale: [0, 3, 5, 7, 10, 12], chord: [0, 3, 7], tempo: 66, arpType: "triangle", padType: "sine" },
};

const semi = (root: number, s: number): number => root * Math.pow(2, s / 12);

export class MusicPlayer {
  private readonly gain: GainNode;
  private current: AudioBufferSourceNode | null = null;
  private currentGain: GainNode | null = null; // 10.5J: ganho por faixa (crossfade)
  private readonly cache = new Map<string, AudioBuffer>();
  private region: string | null = null;
  private volume = 0.5;

  constructor(private readonly ctx: AudioContext) {
    this.gain = ctx.createGain();
    this.gain.gain.value = this.volume;
    this.gain.connect(ctx.destination);
  }

  setVolume(v: number): void {
    this.volume = v;
    // rampa curta evita clique ao mexer no slider
    this.gain.gain.setTargetAtTime(v, this.ctx.currentTime, 0.05);
  }

  /** Toca (em loop) a música da região. No-op se já é a atual. */
  async play(regionId: string): Promise<void> {
    if (this.region === regionId && this.current) return;
    this.region = regionId;
    let buf = this.cache.get(regionId);
    if (!buf) {
      buf = await this.render(regionId);
      this.cache.set(regionId, buf);
      if (this.region !== regionId) return; // trocou de região enquanto renderizava
    }
    this.swap(buf);
  }

  stop(): void {
    if (this.current) {
      try { this.current.stop(); } catch { /* já parado */ }
      this.current = null;
    }
    this.region = null;
  }

  /** 10.5J — CROSSFADE na troca de região: a faixa antiga some em fade enquanto a nova entra
   * (antes cortava seco na transição de mapa). */
  private swap(buf: AudioBuffer): void {
    const t = this.ctx.currentTime;
    const FADE = 1.6;
    if (this.current) {
      const old = this.current;
      const og = this.currentGain;
      if (og) {
        og.gain.setValueAtTime(og.gain.value, t);
        og.gain.linearRampToValueAtTime(0.0001, t + FADE);
      }
      try { old.stop(t + FADE + 0.05); } catch { /* já parado */ }
    }
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(1, t + FADE);
    src.connect(g).connect(this.gain);
    src.start(t);
    this.current = src;
    this.currentGain = g;
  }

  /** Renderiza um loop de N compassos (pad sustentado + arpejo esparso) — costura sem emenda. */
  private async render(regionId: string): Promise<AudioBuffer> {
    const m = MOODS[regionId] ?? MOODS.default;
    const sr = 44100;
    const beat = 60 / m.tempo;
    const bars = 4;
    const steps = bars * 8; // colcheias
    const loopLen = bars * 4 * beat; // 4/4
    const oac = new OfflineAudioContext(1, Math.ceil(sr * loopLen), sr);

    // PAD: acorde grave sustentado o loop todo (bem suave)
    for (const s of m.chord) {
      const f = semi(m.root, s) / 2; // uma oitava abaixo
      const o = oac.createOscillator();
      o.type = m.padType;
      o.frequency.value = f;
      const g = oac.createGain();
      g.gain.setValueAtTime(0, 0);
      g.gain.linearRampToValueAtTime(0.05, 1.5);
      g.gain.setValueAtTime(0.05, loopLen - 1.5);
      g.gain.linearRampToValueAtTime(0.0001, loopLen);
      o.connect(g).connect(oac.destination);
      o.start(0);
      o.stop(loopLen);
    }

    // ARPEJO: notas esparsas subindo/descendo pela escala (deixa "vivo" sem encher)
    let idx = 0;
    for (let i = 0; i < steps; i++) {
      if (i % 2 === 1) continue; // toca a cada 2 colcheias (semínima) — espaçado
      const t = i * (loopLen / steps);
      const deg = m.scale[idx % m.scale.length];
      idx++;
      const f = semi(m.root, deg);
      const o = oac.createOscillator();
      o.type = m.arpType;
      o.frequency.value = f;
      const g = oac.createGain();
      const dur = beat * 0.9;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.09, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, Math.min(loopLen, t + dur));
      // filtro suave p/ tirar aspereza
      const lp = oac.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 2200;
      o.connect(lp).connect(g).connect(oac.destination);
      o.start(t);
      o.stop(Math.min(loopLen, t + dur + 0.05));
    }

    return await oac.startRendering();
  }
}
